import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import FormDrawer from "components/__new/common/form-drawer";
import { ButtonGroupSelector, ButtonLoader } from "../common/form";
import PageContent from "../common/PageContent";
import Maybe from "../common/Maybe";
import useUrlQueryParams from "hooks/useUrlQueryParams";
import processParams from "utils/processParams";
import useMultiSelect from "hooks/useMultiSelect";
import pluralize from "utils/pluralize";
import Table from "../common/Table";
import { ReactComponent as IconArrowLeft } from "assets/images/icons/project-icons/arrow-left.svg";
import { toast } from "react-toastify";
import * as employeesAction from "store/entities/employees/action";
import * as groupsAction from "store/entities/groups/action";
import Pagination from "../common/pagination";
import CustomSwitch from "../common/form/CustomSwitch";
import EmptyUI from "../common/EmptyUI";


const InviteEmployee = (props) => {
    const {isOpen, setIsOpen} = props;
    const dispatch = useDispatch();
    const {urlQueryParams} = useUrlQueryParams();
    const { selections, toggleMultiSelectHeader, getMultiSelectBody, clearSelections } = useMultiSelect();
    const { employeesLoading, employees, inviteToOdpLoading, inviteToOdp } = useSelector((s) => s.entities.employees);
    const { getAllGroupsLoading: groupsLoading, allGroups: groups } = useSelector((s) => s.entities.groups);
    
    const [view, setView] = useState("employees"); // employees or groups
    const [isOpenReviewSelection, setIsOpenReviewSelection] = useState(false);
    const [listParams, setListParams] = useState({
        "page": 1,
        "limit": 20,
    });
    const [selectionOptions, setSelectionOptions] = useState([
        {
            label: "Employees",
            value: "employees",
            selected: true,
        },
        {
            label: "Groups",
            value: "groups",
            selected: false,
        }
    ])
    
    const updateSelectionOptions = (e) => {
        let data = selectionOptions.map((option) => {
            return {
                ...option,
                selected: option.value === e,
            }
        });
        setView(e);
        setSelectionOptions(data);
    }
    
    const toggleSelectAll = () => {
        const ele = document.querySelector(".multi-select-header");
        if (ele?.checked === true){
            ele.checked = false;
        }
        else{
            ele.checked = true;
        }
        toggleMultiSelectHeader();
    }
    
    const getContent = () => {
        if (view === "employees" && employeesLoading === false && Object.keys(employees).length === 0) getEmployees();
        else if (view === "groups" && groupsLoading === false && Object.keys(groups).length === 0) getGroups();
    }
    
    const getEmployees = () => {
        let params = {...listParams, status: ""};

        params = processParams(urlQueryParams, params, "page", "number", 1, "page");
        params = processParams(urlQueryParams, params, "limit", "number", 20, "limit");
        // params = processParams(urlQueryParams, params, "search", "string", "", "search");
        params = processParams(urlQueryParams, params, "search", "string", "", "fullName");
        params = processParams(urlQueryParams, params, "startDate", "string", "", "startDate");
        params = processParams(urlQueryParams, params, "endDate", "string", "", "endDate");

        setListParams(params);        
        dispatch(employeesAction.getAllEmployees(params));
    }

    const getGroups = () => {   
        dispatch(groupsAction.getAllGroups());
    }
    
    const submitForm = async (e) => {
        e.preventDefault();
        if (selections.length > 0){
            setIsOpen(false);
            setIsOpenReviewSelection(true);
        }
        else{
            toast.error("Please make your selection(s)");
        }
    }
    
    const inviteEmployees = () => {
        if (view === "employees"){
            const employeeIds = selections.map((selection) => JSON.parse(selection)?._id);
            dispatch(employeesAction.inviteEmployees({employeeIds}));
        }
        else if (view === "groups"){
            const groupIds = selections.map((selection) => JSON.parse(selection)?._id);
            dispatch(employeesAction.inviteGroups({groupIds}));
        }
    }
    
    useEffect(() => {
        if (isOpen === true){
            getContent();
        }
        // eslint-disable-next-line
    }, [location, view]);
    
    useEffect(() => {
        clearSelections();
        // eslint-disable-next-line
    }, [isOpen, view]);
    
    useEffect(() => {
        if (inviteToOdp?.status === "success"){
            dispatch(employeesAction.resetInviteEmployeeGroup());
            setIsOpenReviewSelection(false);
        }
        // eslint-disable-next-line
    }, [inviteToOdp]);
    
    return (
        <>
            <FormDrawer display="center" size="lg" isOpen={isOpen} setIsOpen={setIsOpen}>
                <div className="p-6">
                    <FormDrawer.Title
                        title="Invite Employee"
                        description="Invite  Employee to get enrolled to use On-Demand Pay"
                        showCloseButton={true}
                        close={() => setIsOpen(false)}
                    />
                    
                    <div className="mt-6">
                        <div className="w-48 p-1 mb-4 bg-ep-gray-200 rounded-lg">
                            <ButtonGroupSelector
                                selectOptions={selectionOptions}
                                onValueChange={(e) => updateSelectionOptions(e)}
                                design="slide-tab-toggle"
                            />
                        </div>
                    </div>
                    
                    {view === "employees" &&
                        <>
                            <div className="flex items-center justify-between mt-4 pb-4 mb-2 border-b border-gray-100">
                                <div className="font-medium">
                                    Select All Employees
                                </div>
                                <div>
                                    <input type="checkbox" className="form-checkbox w-5 h-5 multi-select-header hidden" onChange={toggleMultiSelectHeader} />
                                    <CustomSwitch
                                        active={document.querySelector(".multi-select-header")?.checked}
                                        hideSwitchLabel={true}
                                        onChange={toggleSelectAll}
                                    />
                                </div>
                            </div>
                        
                            <PageContent
                                loading={employeesLoading}
                                data={employees?.employees?.docs}
                                loadingText="Loading employees..."
                                emptyUI={<EmptyUI
                                    text="No Employees Created Yet"
                                    subText={<Link to="/employees/add" className="text-ep-primary border-b border-ep-primary">Click here to add new employee</Link>}
                                />}
                            />
                            
                            <Maybe condition={employeesLoading === false}>
                                <form onSubmit={submitForm}>
                                    <div className="pt-2 max-h-[22rem] overflow-auto px-1">
                                        {employeesLoading === false && employees?.employees?.docs?.map((employee) =>
                                            <label key={employee?._id} className="flex items-center mb-6 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    className="form-checkbox w-5 h-5 multi-select mr-3"
                                                    onChange={getMultiSelectBody}
                                                    checked={selections?.filter((selection) => JSON.parse(selection)._id === employee._id).length === 1}
                                                    value={JSON.stringify(employee)}
                                                />
                                                <div className="mr-3 font-medium capitalize">
                                                    {employee?.firstName ?? "-"} {employee?.lastName}
                                                </div>
                                                <div className="text-fade">
                                                    {employee?.email ?? "-"}
                                                </div>
                                            </label>
                                        )}
                                    </div>
                                    
                                    <Maybe condition={employeesLoading === false && employees?.employees?.docs?.length > 0 && employees?.employees?.totalDocs > listParams.limit}>
                                        <div>
                                            <Pagination data={employees?.employees} />
                                        </div>
                                    </Maybe>
                                    
                                    <Maybe condition={employees?.employees?.docs?.length > 0}>
                                        <div className="pt-4 mt-4 flex justify-end space-x-4 border-t border-gray-100">
                                            <ButtonLoader type="submit" loading={false} className="btn  btn-lg btn-block btn-transparent-primary">
                                                Add
                                            </ButtonLoader>
                                        </div>
                                    </Maybe>
                                </form>
                            </Maybe>
                        </>
                    }
                    
                    {view === "groups" &&
                        <>
                            <div className="flex items-center justify-between mt-4 pb-4 mb-2 border-b border-gray-100">
                                <div className="font-medium">
                                    Select All Groups
                                </div>
                                <div>
                                    <input type="checkbox" className="form-checkbox w-5 h-5 multi-select-header hidden" onChange={toggleMultiSelectHeader} />
                                    <CustomSwitch
                                        active={document.querySelector(".multi-select-header")?.checked}
                                        hideSwitchLabel={true}
                                        onChange={toggleSelectAll}
                                    />
                                </div>
                            </div>
                        
                            <PageContent
                                loading={groupsLoading}
                                data={groups?.policies}
                                loadingText="Loading groups..."
                                emptyUI={<EmptyUI
                                    text="No Groups Created Yet"
                                    subText={<Link to="/groups/create" className="text-ep-primary border-b border-ep-primary">Click here to create new group</Link>}
                                />}
                            />
                            
                            <Maybe condition={groupsLoading === false}>
                                <form onSubmit={submitForm}>
                                    <div className="py-2 max-h-[22rem] overflow-auto px-1">
                                        {groupsLoading === false && groups?.policies?.map((group) =>
                                            <label key={group?._id} className="flex items-center mb-6 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    className="form-checkbox w-5 h-5 multi-select mr-3"
                                                    onChange={getMultiSelectBody}
                                                    checked={selections?.filter((selection) => JSON.parse(selection)._id === group._id).length === 1}
                                                    value={JSON.stringify(group)}
                                                />
                                                <div className="mr-3 font-medium">
                                                    {group.name ?? "-"}
                                                </div>
                                                <div className="text-fade">
                                                    {group.employeeCount ?? "-"} {pluralize(group.employeeCount ?? 0, "Employee", "Employees")}
                                                </div>
                                            </label>
                                        )}
                                    </div>
                                    
                                    <Maybe condition={groups?.policies?.length > 0}>
                                        <div className="pt-4 mt-4 flex justify-end space-x-4 border-t border-gray-100">
                                            <ButtonLoader type="submit" loading={false} className="btn  btn-lg btn-block btn-transparent-primary">
                                                Add
                                            </ButtonLoader>
                                        </div>
                                    </Maybe>
                                </form>
                            </Maybe>
                        </>
                    }
                    
                </div>
            </FormDrawer>
            
            
            <FormDrawer display="center" size="2xl" isOpen={isOpenReviewSelection} setIsOpen={setIsOpenReviewSelection}>
                <div className="p-6">
                    <div className="lg:flex">
                        <div className="w-full lg:w-1/2">
                            <div className="page-title flex">
                                <div onClick={() => {setIsOpenReviewSelection(false); setIsOpen(true)}} className="w-10 h-10 pr-2 mr-4 flex items-center bg-[#ECECEC] hover:bg-gray-300 rounded-md cursor-pointer">
                                    <IconArrowLeft className="relative left-2" />
                                </div>
                                Review Selection
                            </div>
                        </div>
                        <div className="w-full lg:w-1/2 flex mt-4 lg:mt-0 lg:justify-end space-x-3"></div>
                    </div>
                    
                    <div className="mt-8 font-semibold">
                        <Maybe condition={view === "employees"}>
                            {selections?.length} Employee {pluralize(selections?.length ?? 0, "Entry", "Entries")}
                        </Maybe>
                        <Maybe condition={view === "groups"}>
                            {selections?.length} Group {pluralize(selections?.length ?? 0, "Entry", "Entries")}
                        </Maybe>
                    </div>
                    
                    <div className="mt-4">
                        <Table>
                            <Table.Head>
                                {view === "employees" &&
                                    <>
                                        <th>First Name</th>
                                        <th>Last Name</th>
                                        <th>Email Address</th>
                                        <th>Role</th>
                                    </>
                                }
                                {view === "groups" &&
                                    <>
                                        <th>Group Name</th>
                                        <th>Number of Employees</th>
                                        <th>Salary Auto-Withdrawals</th>
                                        <th>Withdrawal Limit</th>
                                    </>
                                }
                            </Table.Head>
                            <Table.Body>

                                {selections?.map((selection) => {
                                    if (view === "employees"){
                                        const employee = JSON.parse(selection);
                                        return (
                                            <tr key={employee._id}>
                                                <td className="capitalize">
                                                    {employee.firstName ?? "-"}
                                                </td>
                                                <td className="capitalize">
                                                    {employee.lastName ?? "-"}
                                                </td>
                                                <td>
                                                    {employee.email ?? "-"}
                                                </td>
                                                <td>
                                                    {employee.jobTitle ?? "-"}
                                                </td>
                                            </tr>
                                        )
                                    }
                                    else if (view === "groups"){
                                        const group = JSON.parse(selection);
                                        return (
                                            <tr key={group._id}>
                                                <td>
                                                    {group.name ?? "-"}
                                                </td>
                                                <td>
                                                    {group.employeeCount ?? "-"} {pluralize(group.employeeCount, "employee", "employees")}
                                                </td>
                                                <td>
                                                    {group.autoWithdrawals ? "Allow" : "Require-Approval"}
                                                </td>
                                                <td>
                                                    {group.limit ? group.limit+"%" : "-"}
                                                </td>
                                            </tr>
                                        )
                                    }
                                    return null;
                                })}

                            </Table.Body>
                        </Table>
                    </div>
                    
                    <div className="pt-10 mt-10 flex justify-end border-t border-gray-100">
                        <ButtonLoader type="button" onClick={inviteEmployees} loading={inviteToOdpLoading} className="btn btn-lg btn-primary order-2 ml-4">
                            Invite
                        </ButtonLoader>
                        <button type="button" onClick={() => {setIsOpenReviewSelection(false); setIsOpen(true)}} className="btn btn-lg btn-transparent-primary order-1">
                            Cancel
                        </button>
                    </div>
                </div>
            </FormDrawer>
        </>
    )
}


export default InviteEmployee;